import { Page, Planogram } from "../../components";
import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";
import { omniAtom } from "../../utils/atoms";
import { pages } from "../../utils";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { useModifiedPog } from "../../hooks/useModifiedPogAtom";
import { useApi } from "../../api";
import { useEffect, useState } from "react";
import { useLoaders } from "../../components/InlineLoader";
import { RoutePostCategories_categoryId_Stores_storeId_FixturesPog } from "@CommonApi/categories/_categoryId_/stores/_storeId_/fixturesPog";
import { sleep } from "../../utils/sleep";
import { PlanogramToolbar } from "../../components/Planogram/components/Toolbar/PlanogramToolbar";
import { Button, Flex } from "../../elements";
import { PlanogramHovererDetails } from "../../components/PlanogramExtensions/PlanogramHovererDetails/PlanogramHovererDetails";
import { PlanogramMetrics } from "../../components/PlanogramExtensions/PlanogramMetrics";
import { useParams } from "src/utils/useParams";
import { RoutePostFixtureGuessing } from "@CommonApi/fixtures-guessing";
import { useNavigation } from "src/utils/navigation";
import { useModalImagineShelvesSettings } from "src/modals/ImagineShelvesSettings/store/actions";

const pogAtom = omniAtom({
  prefix: pages.imagineShelvesFixturesGuessing_fixturesGuessingId_,
  key: "pog",
  value: null as null | Pog,
});

export const PageImagineShelvesFixturesGuessing = () => {
  const { fixturesGuessingId } = useParams<{
    fixturesGuessingId: number;
  }>();
  const { pog, setPog, revertPog, isModified, savePogAsOriginal } = useModifiedPog(pogAtom);
  const { getFixturesGuessingPogApi, postCategories_categoryId_Stores_storeId_FixturesPogApi } =
    useApi();
  const { hasLoader } = useLoaders();
  const { navigate } = useNavigation();
  const { openModalImagineShelvesSettings, imagineShelvesSettingsData } =
    useModalImagineShelvesSettings();

  const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
  const isSaveLoading = hasLoader(RoutePostCategories_categoryId_Stores_storeId_FixturesPog);
  const [categoryId, setCategoryId] = useState<null | number>(null);
  const [storeId, setStoreId] = useState<null | number>(null);

  const loadFixturesGuessingPog = async (fixturesGuessingId: number) => {
    const response = await getFixturesGuessingPogApi({
      fixturesGuessingId: String(fixturesGuessingId),
    });
    if (response) {
      setPog(response.fixturesGuessingPog);
      setCategoryId(response.categoryId);
      setStoreId(response.storeId);
    }
  };

  const onSave = async () => {
    if (!pog) return;

    const response = await postCategories_categoryId_Stores_storeId_FixturesPogApi(
      { categoryId: String(categoryId), storeId: String(storeId) },
      pog.toNonCircular(),
    );

    if (response !== null) {
      setIsSaveSuccessful(true);
      await sleep(1000);
      savePogAsOriginal();
      setIsSaveSuccessful(false);
    }
  };

  const onClickImagine = async () => {
    if (categoryId !== null && storeId !== null) {
      openModalImagineShelvesSettings({
        categoryId: categoryId,
        storeIds: [storeId],
      });
    }
  };

  useEffect(() => {
    loadFixturesGuessingPog(fixturesGuessingId);
  }, [fixturesGuessingId]);

  useEffect(() => {
    return () => {
      setPog(null);
    };
  }, []);

  useEffect(() => {
    if (imagineShelvesSettingsData?.started === true && categoryId !== null) {
      setTimeout(() => {
        navigate(pages.imagineShelvesCategories_categoryId_, { categoryId });
      }, 2000);
    }
  }, [imagineShelvesSettingsData]);

  return (
    <Page>
      <MerchflowHeader
        imagine="SHELVES"
        categoryId={categoryId || undefined}
        storeId={storeId || undefined}
      />

      <Flex width="100%" align="center" gap="5px">
        <PlanogramToolbar pogAtom={pogAtom} showProducts={false} />

        <Button
          color="greenSmoke"
          onClick={onClickImagine}
          isEnabled={categoryId !== null && storeId !== null}
          isLoading={hasLoader(`POST_${RoutePostFixtureGuessing}`)}
          isSuccessful={imagineShelvesSettingsData?.started}
        >
          Imagine with AI
        </Button>
      </Flex>

      <Flex minHeight="0" flexGrow column>
        <Planogram
          pogAtom={pogAtom}
          isEditable
          isPanelVisible
          isPog
          isWithBorder
          showPanelProducts={false}
        />
      </Flex>

      <PlanogramHovererDetails />

      <Flex justify="between" align="center">
        <PlanogramMetrics pogAtom={pogAtom} />

        <Flex gap="10px">
          {isModified && (
            <Button
              color="red"
              variant="inverted"
              isDisabled={isSaveLoading || isSaveSuccessful}
              onClick={revertPog}
            >
              Revert Changes
            </Button>
          )}

          <Button isLoading={isSaveLoading} isSuccessful={isSaveSuccessful} onClick={onSave}>
            Save
          </Button>
        </Flex>
      </Flex>
    </Page>
  );
};
